import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { KeyboardShortcut } from "../components/KeyboardShortcut";
import { useSheetContainer } from "../hooks/useSheetContainer";
import { cn } from "../lib/utils";
import * as React from "react";
const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipPortal = TooltipPrimitive.Portal;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React.forwardRef(({
  className,
  sideOffset = 4,
  mountPortal = true,
  mountPortalContainer,
  ...props
}, ref) => {
  const content = /*#__PURE__*/React.createElement(TooltipPrimitive.Content, {
    ref: ref,
    sideOffset: sideOffset,
    className: cn("z-50 max-w-sm overflow-hidden whitespace-pre-wrap break-words rounded-lg", "bg-primary text-primary-50 text-xs", "px-3 py-1.5", "shadow-[inset_0px_1px_4px_0px_rgba(255,255,255,0.1)] dark:shadow-none", "origin-[var(--radix-tooltip-content-transform-origin)]", "animate-in fade-in-0 zoom-in-95 duration-150 ease-emphasized", "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-100", "motion-reduce:animate-none", className || ""),
    ...props
  });
  const container = useSheetContainer(mountPortalContainer);
  return mountPortal ? /*#__PURE__*/React.createElement(TooltipPrimitive.Portal, {
    container: container
  }, content) : content;
});
/**
 * Displays a brief, contextual label when the user hovers or focuses a trigger — ideal
 * for clarifying icon-only controls or surfacing a keyboard shortcut. Use this simple
 * form (a `trigger` plus a `label`, with an optional `shortcut`) for most cases; compose
 * `TooltipProvider` / `TooltipRoot` / `TooltipTrigger` / `TooltipContent` for full
 * control over placement and timing. Never place essential information or interactive
 * elements only inside a tooltip — it is not reachable on touch and disappears on blur.
 *
 * @summary Hover/focus label for a trigger.
 */
const Tooltip = React.forwardRef(({
  trigger,
  tooltipTriggerAsChild = false,
  label,
  shortcut,
  delayDuration = 300,
  ...props
}, ref) => /*#__PURE__*/React.createElement(TooltipProvider, {
  delayDuration: delayDuration,
  skipDelayDuration: 0
}, /*#__PURE__*/React.createElement(TooltipRoot, {
  disableHoverableContent: true
}, /*#__PURE__*/React.createElement(TooltipTrigger, {
  asChild: tooltipTriggerAsChild
}, trigger), /*#__PURE__*/React.createElement(TooltipContent, {
  ...props,
  ref: ref
}, /*#__PURE__*/React.createElement("div", {
  className: "inline-flex items-center gap-2"
}, label, shortcut && /*#__PURE__*/React.createElement(KeyboardShortcut, {
  shortcut: shortcut,
  className: "text-xs text-primary-200"
}))))));
Tooltip.displayName = "Tooltip";
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
export { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger };
//# sourceMappingURL=Tooltip.js.map